import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {gsApi} from "../auth/auth";
import {
  addSerializedRelationships,
  getIDRelationship,
  getOpts,
  getResponse,
  getSerializedData,
  longDelay,
} from "../helpers/JSONapi";
import {
  createThunkResponse,
  deleteThunkResponse,
  getThunkResponse,
  saveThunkResponse,
  teamFlights,
  teamPreflights,
  tournament,
  tournamentFlights,
  tournamentRounds,
} from "../app/api";

export const getTournamentFlights = createAsyncThunk(
  "flight/getTournamentFlights",
  async (tournamentID) => {
    const response = await gsApi.get(
      `${tournament}/${tournamentID}/tournament-flights`
    );
    return response;
  }
);

export const getTournamentPreFlights = createAsyncThunk(
  "flight/getTournamentPreFlights",
  async (tournamentID) => {
    const response = await gsApi.get(
      `${tournament}/${tournamentID}/team-preflights`
    );
    return response;
  }
);

export const getTournamentFlight = createAsyncThunk(
  "flight/getTournamentFlight",
  async (flightID) => {
    const response = await gsApi.get(`${tournamentFlights}/${flightID}`);
    return response;
  }
);

export const getRoundFlight = createAsyncThunk(
  "flight/getRoundFlight",
  async (roundID) => {
    const response = await gsApi.get(
      `${tournamentRounds}/${roundID}/flight-rounds`
    );
    return response;
  }
);

//save

export const saveTournamentFlight = createAsyncThunk(
  "flight/saveTournamentFlight",
  async (flight, thunkAPI) => {
    let serializedData = getSerializedData(
      flight,
      "tournament-flights",
      getOpts(flight, [...getIDRelationship("tournament")])
    );
    return await getResponse(
      gsApi.patch,
      `${tournamentFlights}/${flight.id}`,
      serializedData,
      thunkAPI
    );
  }
);

const updateRemoteTeamFlights = async (teamFlights, thunkAPI) => {
  for (const tf of teamFlights) {
    for (const ft of tf.teamFlights) {
      if (ft.moved) {
        let update = { ...ft };
        update["tournament-flight"] = tf;
        update["tournament-team"] = { id: ft.teamScores.teamKey };
        const response = await updateRemoteTeamFlight(update, thunkAPI);
        if(!response?.id){
          return response
        }
      }
    }
  }
};

const updateRemoteTeamFlight = async (teamFlight, thunkAPI) => {
  let serializedData = getSerializedData(
    teamFlight,
    "team-flights",
    getOpts(teamFlight, [
      ...getIDRelationship("tournament"),
      ...getIDRelationship("tournament-flight"),
      ...getIDRelationship("tournament-team"),
    ])
  );

  return await getResponse(
    gsApi.patch,
    `${teamFlights}/${teamFlight.id}`,
    serializedData,
    thunkAPI
  );
};

export const saveTeamFlight = createAsyncThunk(
  "flight/saveTeamFlight",
  updateRemoteTeamFlight
);

export const saveTeamFlights = createAsyncThunk(
  "flight/saveTeamFlights",
  updateRemoteTeamFlights
);

export const preflightTournamentAndFlights = createAsyncThunk(
  "flight/preflightTournamentAndFlights",
  async (tournamentAndFlights, thunkAPI) => {
    const { dealId, pipedriveSyncedAt, ...tournamentData } = tournamentAndFlights;
    let serializedData = getSerializedData(
      tournamentData,
      "tournaments",
      getOpts(tournamentData, [])
    );

    addSerializedRelationships(
      serializedData.data,
      "tournament-flights",
      tournamentAndFlights["tournament-flights"],
      [
        "flight-rounds",
        "flight-round-tees",
        "round-tee",
        "round-course",
        "tournament",
        "leaderboard-type-setting",
        "tournament-round",
        "tournament-flight",
        "tournament",
        "flight-round",
      ]
    );

    return await getResponse(
      gsApi.patch,
      `${tournament}/${tournamentAndFlights.id}/preflighting-tournament-and-flights`,
      serializedData,
      thunkAPI
    );
  }
);

export const savePreflight = createAsyncThunk(
  "flight/savePreflight",
  async (flight, thunkAPI) => {
    let serializedData = getSerializedData(
      flight,
      "team-preflights",
      getOpts(flight, [
        ...getIDRelationship("preflightSet"),
        ...getIDRelationship("tournamentTeam"),
      ])
    );
    return await getResponse(
      gsApi.patch,
      `${teamPreflights}/${flight.id}`,
      serializedData,
      thunkAPI
    );
  }
);

export const autoAssignFlights = createAsyncThunk(
  "flight/autoAssignFlights",
  async (assignSettings, thunkAPI) => {
    if (assignSettings?.tournamentID && assignSettings?.teamIds) {
      return await getResponse(
        gsApi.post,
        `${tournament}/${assignSettings.tournamentID}/auto-preflight`,
        { teamIds: assignSettings.teamIds },
        thunkAPI
      );
    }
  }
);

//create

export const createTournamentFlight = createAsyncThunk(
  "flight/createTournamentFlight",
  async (flight, thunkAPI) => {
    let serializedData = getSerializedData(
      flight,
      "tournament-flights",
      getOpts(flight, [
        ...getIDRelationship("tournament"),
        ...getIDRelationship("leaderboard-type-setting"),
      ])
    );
    return await getResponse(
      gsApi.post,
      `${tournamentFlights}`,
      serializedData,
      thunkAPI
    );
  }
);

//delete

export const removeTournamentFlight = createAsyncThunk(
  "flight/removeTournamentFlight",
  async (flightID) => {
    const response = await gsApi.delete(`${tournamentFlights}/${flightID}`, {
      data: {},
    });
    return response;
  }
);

//calculate flights

export const createTeamFlights = createAsyncThunk(
  "flight/createTeamFlights",
  async (tournamentID) => {
    const response = await gsApi.post(
      `${tournament}/${tournamentID}/create_team_flights`,
      {}
    );
    return response;
  }
);

export const deleteTeamFlights = createAsyncThunk(
  "flight/deleteTeamFlights",
  async (tournamentID) => {
    const response = await gsApi.delete(
      `${tournament}/${tournamentID}/delete_team_flights`,
      { data: {} }
    );
    return response;
  }
);

export const removeAllFlightAssignments = createAsyncThunk(
  "flight/removeAllFlightAssignments",
  async (tournamentID) => {
    const response = await gsApi.delete(
      `${tournament}/${tournamentID}/team-preflight-assignments`,
      { data: {} }
    );
    return response;
  }
);

//replace all occurences of flight with the name of your new slice
//a save that can move teams in or out of flights leaves the tournament's word on
//whether any team has a flight out of date until the tournament is loaded again
const changesFlightAssignments = (callback) => (state, action) => {
  state.preflightAssignmentsChanged = true;
  callback(state, action);
};

export const flightSlice = createSlice({
  name: "flights",
  initialState: {
    tournamentFlights: [],
    roundFlights: [],
    tournamentTeamPreflights: [],
    currentTournamentFlight: {},
    notifications: [],
    loading: [],
    preflightAssignmentsChanged: false,
  },
  //be sure to export any reducers on line 65
  reducers: {
    preflightAssignmentsRefreshed: (state) => {
      state.preflightAssignmentsChanged = false;
    },
    setTournamentFlights: (state, action) => {
      state.tournamentFlights = action.payload;
    },
    updateCurrentFlight: (state, action) => {
      state.currentTournamentFlight = action.payload;
    },
    updateTeamFlight: (state, action) => {
      let preflightIndex = -1;
      let flightTeamIndex = -1;
      state.tournamentTeamPreflights.forEach((ttpf, index) => {
        preflightIndex = index;
        flightTeamIndex = ttpf.teamPreflights.findIndex(
          (tpf) => tpf.id === action.payload.update.id
        );
      });
      if (preflightIndex !== -1 && flightTeamIndex !== -1) {
        state.tournamentTeamPreflights[preflightIndex].teamPreflights[
          flightTeamIndex
        ] = action.payload.update;
      }
      // add or update team to flight
      if(!state?.currentTournamentFlight?.id) {
        return
      }
      if (action.payload.update.position) {
        const updatedTeamFlight = {
          id: action.payload.update.id,
          flightName: action.payload.update.flight,
          flightPosition: state.currentTournamentFlight.position,
          preflightPosition: action.payload.update.position,
          teamName:
            action.payload.update.tournamentTeam.name ||
            action.payload.update.tournamentTeam.contactName,
          tournamentFlight: { id: state.currentTournamentFlight.id },
          tournamentTeam: { id: action.payload.update.tournamentTeam.id },
        };
        // find existing team and update
        const teamFlightIndex = state.tournamentFlights
          .find((f) => f.id === state.currentTournamentFlight.id)
          .teamFlights.findIndex((tf) => tf.id === action.payload.update.id);
        if (teamFlightIndex === -1) {
          // otherwise add new team to flight
          state.tournamentFlights
            .find((f) => f.id === state.currentTournamentFlight.id)
            .teamFlights.push(updatedTeamFlight);
        } else {
          state.tournamentFlights.find(
            (f) => f.id === state.currentTournamentFlight.id
          ).teamFlights[teamFlightIndex] = updatedTeamFlight;
        }
      } else {
        // remove team from flight
        state.tournamentFlights.find(
          (f) => f.id === state.currentTournamentFlight.id
        ).teamFlights = state.tournamentFlights
          .find((f) => f.id === state.currentTournamentFlight.id)
          .teamFlights.filter((tf) => tf.id !== action.payload.update.id);
      }
    },
    updateTeamResult: (state, action) => {
      state.tournamentFlights = action.payload;
    },
    updateFlightTeams: (state, action) => {
      let index = state.tournamentFlights.findIndex?.(
        (f) =>
          f.id === action.payload.id &&
          f.leaderboardTypeSetting.id ===
            action.payload.leaderboardTypeSetting.id
      );
      state.tournamentFlights[index] = action.payload;
    },
    addRoundFlight: (state, action) => {
      const fr = action.payload;
      let index = state.roundFlights.findIndex?.(
        (rf) =>
          rf?.tournamentRound?.id === fr?.tournamentRound?.id &&
          rf?.tournamentFlight?.id === fr?.tournamentFlight?.id
      );
      if (index === -1) {
        state.roundFlights.push(fr);
      } else {
        state.roundFlights[index] = fr;
      }
    },
    addNotification: (state, action) => {
      state.notifications.push(action.payload);
    },
    setNotifications: (state, action) => {
      state.notifications = action.payload;
    },
    clearNotifications: (state, action) => {
      state.notifications = [];
    },
  },
  extraReducers: {
    //get tournament flights
    ...getThunkResponse(getTournamentFlights, (state, action) => {
      state.tournamentFlights = action.payload;
    }),
    //get round flights
    ...getThunkResponse(getRoundFlight, (state, action) => {
      action.payload.forEach((fr) => {
        let index = state.roundFlights.findIndex?.((rf) => rf.id === fr.id);
        if (index === -1) {
          state.roundFlights.push(fr);
        } else {
          state.roundFlights[index] = fr;
        }
      });
    }),
    //get tournament team preflights
    ...getThunkResponse(getTournamentPreFlights, (state, action) => {
      state.tournamentTeamPreflights = action.payload;
    }),
    //get tournament flight
    ...getThunkResponse(getTournamentFlight, (state, action) => {
      state.currentTournamentFlight = action.payload;
    }),
    //save flight
    ...saveThunkResponse(
      saveTournamentFlight,
      (state, action) => {
        const flightIndex = state.tournamentFlights.findIndex(
          (tf) => tf.id === action.payload.id
        );
        if (flightIndex !== -1) {
          state.tournamentFlights[flightIndex] = {
            ...state.tournamentFlights[flightIndex],
            ...action.payload,
          };
        }
      },
      "Tournament Flight"
    ),
    //create flight
    ...createThunkResponse(
      createTournamentFlight,
      (state, action) => {
        state.tournamentFlights.push(action.payload);
      },
      "Tournament Flight"
    ),
    // remove flight
    ...deleteThunkResponse(
      removeTournamentFlight,
      changesFlightAssignments((state, action) => {
        state.tournamentFlights = state.tournamentFlights?.filter?.(
          (f) => f.id !== action.meta.arg
        );
      }),
      "Tournament Flight"
    ),
    // remove all flight assignments
    ...deleteThunkResponse(
      removeAllFlightAssignments,
      changesFlightAssignments((state, action) => {
        state.tournamentTeamPreflights = action.payload;
      }),
      "Flight Assignments"
    ),
    // remove all flight assignments
    ...saveThunkResponse(
      autoAssignFlights,
      changesFlightAssignments(() => {}),
      "Flight Teams"
    ),
    ...saveThunkResponse(savePreflight, changesFlightAssignments(() => {}), "Flight Assignment"),
    ...saveThunkResponse(
      createTeamFlights,
      changesFlightAssignments(() => {}),
      "Flights",
      longDelay
    ),
    ...deleteThunkResponse(deleteTeamFlights, changesFlightAssignments(() => {}), "Flights"),
    ...saveThunkResponse(saveTeamFlight, changesFlightAssignments(() => {}), "Team Flight"),
    ...saveThunkResponse(saveTeamFlights, changesFlightAssignments(() => {}), "Team Flighting", 0),
    ...saveThunkResponse(
      preflightTournamentAndFlights,
      changesFlightAssignments((state) => {
        state.roundFlights = [];
      }),
      "preflight"
    ),
  },
});

export const {
  preflightAssignmentsRefreshed,
  setTournamentFlights,
  updateCurrentFlight,
  addRoundFlight,
  updateTeamFlight,
  addNotification,
  clearNotifications,
  updateTeamResult,
  setNotifications,
  updateFlightTeams,
} = flightSlice.actions;

//export any selectors needed
export const selectTournamentFlights = (state) =>
  state?.[flightSlice.name]?.tournamentFlights;

export const selectRoundFlights = (state) =>
  state?.[flightSlice.name]?.roundFlights;

export const selectTournamentTeamPreflights = (state) =>
  state?.[flightSlice.name]?.tournamentTeamPreflights;

export const selectFlightNotifications = (state) =>
  state?.[flightSlice.name]?.notifications;

export const selectFlightLoading = (state) =>
  state?.[flightSlice.name]?.loading;

export const selectPreflightAssignmentsChanged = (state) =>
  state?.[flightSlice.name]?.preflightAssignmentsChanged;

export const selectCurrentTournamentFlight = (state) =>
  state?.[flightSlice.name]?.currentTournamentFlight;

export default flightSlice.reducer;

//ADD reducer to store.js
